import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import messageFromError from "../../utils/errorUtils";
import {Button, Form, FormControl, FormLabel, Modal, Spinner, Tab, Tabs} from "react-bootstrap";
import {getRoles, inviteUser, registerUser} from "API/usersApi";
import PasswordMeter from "../PasswordMeter";
import {useTranslation} from "react-i18next";
import useClaims from "../../Hooks/useClaims";
import {CREATE_USERS_ALL} from "../../Claims";

const RegisterUserModal = ({companyId, show, onDismiss, onRegistered}) => {

    const {t} = useTranslation()
    const {hasClaim} = useClaims()

    const [isLoading, setIsLoading] = useState(false)
    const [user, setUser] = useState({ email: '', password: '', role: '' });
    const [roles, setRoles] = useState([])
    const [selectedTab, setSelectedTab] = useState('invite')


    useEffect(() => {
        async function fetchRoles()
        {
          try {
            const response = await getRoles();
            setRoles(response.data);
            setUser({...user, role: response.data[0].id})
          }
          catch (error) {
            toast.error(messageFromError(error))
          }
        }
        fetchRoles()
    }, [])

    async function register()
    {
        try {
            setIsLoading(true) 

            if (selectedTab === 'invite')
            {
                await inviteUser(user, companyId)
            }
            else
            {
                await registerUser({...user, roles: [user.role]}, companyId)
            }

            toast.success("Success")

            setUser({ email: '', password: '', role: roles[0]?.id || '' })
            onRegistered()
            onDismiss()
        } 
        catch (error) {
            toast.error(messageFromError(error))
        }
        finally
        {
            setIsLoading(false)
        }
    }

    function getButtonDisabled()
    {
        if (selectedTab === 'invite')
        {
            return user.email.length < 3 || !user.role;
        }

        return user.email.length < 3 || !user.password || !user.role;
    }

    function renderEmailAndRole() {
        return (
            <>
                <FormLabel>{t('pages.users.registerUserDialog.email')}</FormLabel>
                <FormControl type='email' placeholder='example@gmail.com' value={user.email} onChange={(e) => {setUser({...user, email: e.target.value})}}/>

                <FormLabel className='mt-3'>{t('pages.users.role')}</FormLabel>
                <Form.Select value={user.role} onChange={(e) => {setUser({...user, role: e.target.value})}}>
                    {roles.map(role => {
                        if (role.id === "rol_KUK7jNZtAxNospIf") {
                            return null; // Skip the "Super Admin" role if the user does not have the CREATE_USERS_ALL claim
                        }

                        return (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        )
                    })}
                </Form.Select>
            </>
        )
    }

    return (
        <Modal show={show} onHide={onDismiss}>
            <Modal.Header closeButton className='modal-header'>
                <Modal.Title>{t('pages.users.registerUserDialog.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Tabs variant='underline'
                      defaultActiveKey={'invite'}
                      className="mb-2 px-2"
                      onSelect={(tab) => setSelectedTab(tab)}>

                    <Tab eventKey={'invite'} title={t('pages.users.registerUserDialog.invite')}>
                        <p className={'ps-1'}><small>{t('pages.users.registerUserDialog.inviteDesc')}</small></p>
                        {renderEmailAndRole()}
                    </Tab>

                    <Tab eventKey={'register'} title={t('pages.users.registerUserDialog.register')}>
                        <p className={'ps-1'}><small>{t('pages.users.registerUserDialog.regDesc')}</small></p>
                        {renderEmailAndRole()}
                        <PasswordMeter
                            className='mt-3'
                            onSufficientPassword={(p) => {
                                setUser((prev) => (
                                    {
                                        ...prev,
                                        password: p
                                    }
                                ))
                            }}
                            onUnSufficientPassword={() => {
                                setUser(prev => (
                                    {
                                        ...prev,
                                        password: ''
                                    }
                                ))
                            }}
                        />
                    </Tab>

                </Tabs>
            </Modal.Body>
            <Modal.Footer>
            {
                    isLoading ? <Spinner animation="border" variant="success" /> :
                    <Button disabled={getButtonDisabled()} variant="success" onClick={register}>
                        {selectedTab === 'invite' ? t('pages.users.registerUserDialog.invite') : t('pages.users.registerUserDialog.register')}
                    </Button>
                }
            </Modal.Footer>
        </Modal>
    );
};

export default RegisterUserModal;